// Reusable "complete your profile" gate + shared-data auto-fill helper.
// Kisi bhi project ko wrap karo <ProfileGate need={['country','phone']}> ... </ProfileGate>
// Agar shared fields khali hon to pehle inhe bharwata hai (ek dafa), phir project khulta hai.
// Ye data master profile se aata/jaata hai — har jagah reuse.

import { useEffect, useState } from 'react';
import { t } from './i18n';

const API = '/api';

function authHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
}

const COUNTRIES = ['Pakistan', 'India', 'Bangladesh', 'United States', 'United Kingdom', 'United Arab Emirates',
  'Saudi Arabia', 'Canada', 'Australia', 'Germany', 'France', 'Turkey', 'Nigeria', 'Indonesia', 'Malaysia', 'Other'];

const C = { surface: '#fff', border: '#E3E1D9', text: '#2C2C2A', muted: '#5F5E5A', primary: '#0F6E56' };
const inp = { width: '100%', padding: '9px 11px', fontSize: 14, border: `0.5px solid ${C.border}`, borderRadius: 8, boxSizing: 'border-box', marginBottom: 10 };
const lbl = { fontSize: 12, color: C.muted, display: 'block', marginBottom: 4 };

// Field definitions — extend as needed
const FIELD_DEFS = {
  email: { label: t('pg_email'), type: 'text', placeholder: 'you@example.com' },
  address: { label: t('pg_address'), type: 'textarea', placeholder: 'House, street, city' },
  country: { label: t('pg_country'), type: 'country' },
  phone: { label: t('pg_phone'), type: 'text', placeholder: '+92 3xx xxxxxxx' },
  city: { label: t('pg_city'), type: 'text', placeholder: 'Lahore' },
  bio: { label: t('pg_bio'), type: 'textarea', placeholder: 'About you' },
};

// Cached master profile so multiple gates in one session don't refetch
let _cache = null;

export async function fetchSharedProfile(force = false) {
  if (_cache && !force) return _cache;
  try {
    const res = await fetch(`${API}/master-profile/`, { headers: authHeaders() });
    const raw = await res.json();
    // master-profile nested hai: {general:{country,phone,...}, merchant:{...}, matrimony:{...}}
    // Shared user-level fields 'general' mein hain — unhe top-level pe flatten karte hain.
    const g = (raw && raw.general) || {};
    _cache = { ...g, _raw: raw };
  } catch (_) { _cache = {}; }
  return _cache;
}

export function invalidateProfileCache() { _cache = null; }

export default function ProfileGate({ need = [], projectName = 'this', children }) {
  const [state, setState] = useState('loading'); // loading | needs | ready
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!localStorage.getItem('token')) { setState('ready'); return; }
    fetchSharedProfile().then((p) => {
      const missing = need.filter((f) => !p[f] || String(p[f]).trim() === '');
      if (missing.length === 0) { setState('ready'); return; }
      const seed = {};
      need.forEach((f) => { seed[f] = p[f] || ''; });
      setForm(seed);
      setState('needs');
    });
  }, []); // eslint-disable-line

  const save = async () => {
    for (const f of need) {
      if (!form[f] || String(form[f]).trim() === '') { setMsg(`${FIELD_DEFS[f]?.label || f} ${t('pg_required')}`); return; }
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/master-profile/save/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      const d = await res.json();
      if (d && (d.success || d.saved || res.ok)) {
        invalidateProfileCache();
        await fetchSharedProfile(true);
        setState('ready');
      } else { setMsg(d.error || 'Save failed'); }
    } catch (_) { setMsg('Save failed — dobara koshish karein'); }
    finally { setSaving(false); }
  };

  if (state === 'loading') return <div style={{ padding: '2rem', textAlign: 'center', fontSize: 13, color: C.muted }}>{t('pg_loading')}</div>;
  if (state === 'ready') return children;

  // needs: show one-time form
  return (
    <div style={{ maxWidth: 480, margin: '2rem auto', padding: '1.5rem', fontFamily: 'sans-serif' }}>
      <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 14, padding: '1.4rem 1.3rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <img src="/logo192.png" alt="" style={{ width: 32, height: 32, borderRadius: 8 }} onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <p style={{ fontSize: 16, fontWeight: 700, margin: 0, color: C.text }}>{t('pg_complete_title')}</p>
            <p style={{ fontSize: 12, color: C.primary, margin: 0 }}>{t('pg_complete_sub')}</p>
          </div>
        </div>
        <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 14px' }}>
          {t('pg_complete_body').replace('{project}', projectName)}
        </p>
        {msg && <p style={{ fontSize: 13, color: '#A32D2D' }}>{msg}</p>}
        {need.map((f) => {
          const def = FIELD_DEFS[f] || { label: f, type: 'text' };
          const set = (v) => setForm({ ...form, [f]: v });
          return (
            <div key={f}>
              <label style={lbl}>{def.label}</label>
              {def.type === 'country'
                ? <select style={inp} value={form[f] || ''} onChange={(e) => set(e.target.value)}>
                    <option value="">{t('pg_select')}</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                : def.type === 'textarea'
                  ? <textarea style={{ ...inp, minHeight: 60 }} value={form[f] || ''} onChange={(e) => set(e.target.value)} placeholder={def.placeholder || ''} />
                  : <input style={inp} value={form[f] || ''} onChange={(e) => set(e.target.value)} placeholder={def.placeholder || ''} />}
            </div>
          );
        })}
        <button onClick={save} disabled={saving} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 700, border: 'none', borderRadius: 9, background: C.primary, color: '#fff', cursor: saving ? 'wait' : 'pointer' }}>
          {saving ? t('pg_saving') : t('pg_save_continue')}
        </button>
      </div>
    </div>
  );
}
